/**
 * AudioRecordingButton - Reusable microphone button for starting audio recordings
 *
 * This is a presentation-only component designed to be flexible and reusable
 * across different screens (CreateRequest, RequestDetail, etc.).
 *
 * Usage:
 * ```tsx
 * <AudioRecordingButton
 *   onPress={handleStartRecording}
 *   disabled={isRecording}
 * />
 * ```
 *
 * Position this button wherever recording functionality is needed - the component
 * has no hardcoded positioning and can be styled via the `style` prop.
 */

import React from 'react';
import { TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import { Mic } from '../icons';

interface AudioRecordingButtonProps {
  /** Callback fired when button is pressed */
  onPress: () => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Size of the button (diameter in pixels) */
  size?: number;
  /** Custom styles to apply to the button container */
  style?: ViewStyle;
  /** Optional test id (E2E) */
  testID?: string;
}

export const AudioRecordingButton: React.FC<AudioRecordingButtonProps> = ({
  onPress,
  disabled = false,
  size = 48,
  style,
  testID,
}) => {
  return (
    <TouchableOpacity
      testID={testID}
      style={[
        styles.button,
        { width: size, height: size, borderRadius: size / 2 },
        disabled && styles.buttonDisabled,
        style,
      ]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <Mic size={20} color={disabled ? '#ccc' : '#666'} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  buttonDisabled: {
    backgroundColor: '#e0e0e0',
    opacity: 0.5,
  },
});
